// Copy text to clipboard
export const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for mobile
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        return true;
      } catch (err) {
        console.error('Fallback copy failed:', err);
        return false;
      } finally {
        textArea.remove();
      }
    }
  } catch (err) {
    console.error('Copy failed:', err);
    return false;
  }
};

// Generate referral link
export const generateReferralLink = (userId) => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/#/register?ref=${userId}`;
};

// Get URL parameters
export const getUrlParam = (param) => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(param);
};

// Get hash parameters
export const getHashParam = (param) => {
  const hash = window.location.hash;
  const queryIndex = hash.indexOf('?');
  if (queryIndex === -1) return null;
  
  const queryString = hash.substring(queryIndex + 1);
  const urlParams = new URLSearchParams(queryString);
  return urlParams.get(param);
};

// Sleep function
export const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Debounce function
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Throttle function
export const throttle = (func, limit) => {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// Check if mobile device
export const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Check if iOS
export const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

// Check if in-app browser (Trust Wallet, etc)
export const isInAppBrowser = () => {
  const ua = navigator.userAgent || navigator.vendor;
  return /FBAN|FBAV|Instagram|Twitter|TrustWallet|MetaMask/i.test(ua);
};

// Truncate text
export const truncateText = (text, maxLength) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// Validate ethereum address
export const isValidAddress = (address) => {
  return /^0x[a-fA-F0-9]{40}$/.test(address);
};

// Parse error message
export const parseError = (error) => {
  if (!error) return 'An unknown error occurred';
  
  // Check for common error patterns
  if (error.reason) return error.reason;
  if (error.message) {
    // Parse revert messages
    if (error.message.includes('user rejected')) {
      return 'Transaction was rejected';
    }
    if (error.message.includes('insufficient funds')) {
      return 'Insufficient funds for transaction';
    }
    if (error.message.includes('E1')) return 'Already registered';
    if (error.message.includes('E2')) return 'Invalid package amount';
    if (error.message.includes('E3')) return 'Sponsor not found';
    if (error.message.includes('E4')) return 'User not found';
    if (error.message.includes('E5')) return 'Account is blacklisted';
    if (error.message.includes('E6')) return 'Package must be equal or higher';
    if (error.message.includes('E7')) return 'Invalid user';
    if (error.message.includes('E8')) return 'Invalid withdrawal amount';
    
    return error.message;
  }
  
  return 'Transaction failed';
};

// Local storage helpers
export const storage = {
  get: (key, defaultValue = null) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (err) {
      return defaultValue;
    }
  },
  set: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (err) {
      return false;
    }
  },
  remove: (key) => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (err) {
      return false;
    }
  }
};

// Vibrate (haptic feedback)
export const vibrate = (pattern = 50) => {
  if ('vibrate' in navigator) {
    navigator.vibrate(pattern);
  }
};
